import {
  Context,
  createContext,
  FC,
  ReactNode,
  useCallback,
  useMemo,
  useReducer,
} from 'react';
import arrayUtil from 'src/utils/array.util';

export type OrderSelectionContextValue = {
  orderIds: number[];
  allocatedOrderIds: number[];
  selectedOrderIds: number[];

  setOrderIds: (value: number[]) => void;
  addOrderIds: (value: number[]) => void;
  removeOrderIds: (value: number[]) => void;
  resetOrderIds: () => void;

  setAllocatedOrderIds: (value: number[]) => void;
  addAllocatedOrderIds: (value: number[]) => void;
  removeAllocatedOrderIds: (value: number[]) => void;
  resetAllocatedOrderIds: () => void;

  setSelectedOrderIds: (value: number[]) => void;
  addSelectedOrderIds: (value: number) => void;
  bulkAddSelectedOrderIds: (value: number[]) => void;
  removeSelectedOrderIds: (value: number) => void;
  bulkRemoveSelectedOrderIds: (value: number[]) => void;
  resetSelectedOrderIds: () => void;
};

type OrderSelectionState = {
  orderIds: number[];
  allocatedOrderIds: number[];
  selectedOrderIds: number[];
};

type SetOrderIds = {
  type: 'SET_ORDER_IDS';
  value: number[];
};
type AddOrderIds = {
  type: 'ADD_ORDER_IDS';
  value: number[];
};
type RemoveOrderIds = {
  type: 'REMOVE_ORDER_IDS';
  value: number[];
};
type ResetOrderIds = {
  type: 'RESET_ORDER_IDS';
};
type SetAllocatedOrderIds = {
  type: 'SET_ALLOCATED_ORDER_IDS';
  value: number[];
};
type AddAllocatedOrderIds = {
  type: 'ADD_ALLOCATED_ORDER_IDS';
  value: number[];
};
type RemoveAllocatedOrderIds = {
  type: 'REMOVE_ALLOCATED_ORDER_IDS';
  value: number[];
};
type ResetAllocatedOrderIds = {
  type: 'RESET_ALLOCATED_ORDER_IDS';
};
type SetSelectedOrderIds = {
  type: 'SET_SELECTED_ORDER_IDS';
  value: number[];
};
type AddSelectedOrderIds = {
  type: 'ADD_SELECTED_ORDER_IDS';
  value: number;
};
type BulkAddSelectedOrderIds = {
  type: 'BULK_ADD_SELECTED_ORDER_IDS';
  value: number[];
};
type RemoveSelectedOrderIds = {
  type: 'REMOVE_SELECTED_ORDER_IDS';
  value: number;
};
type BulkRemoveSelectedOrderIds = {
  type: 'BULK_REMOVE_SELECTED_ORDER_IDS';
  value: number[];
};
type ResetSelectedOrderIds = {
  type: 'RESET_SELECTED_ORDER_IDS';
};

type Action =
  | SetOrderIds
  | AddOrderIds
  | RemoveOrderIds
  | ResetOrderIds
  | SetAllocatedOrderIds
  | AddAllocatedOrderIds
  | RemoveAllocatedOrderIds
  | ResetAllocatedOrderIds
  | SetSelectedOrderIds
  | AddSelectedOrderIds
  | BulkAddSelectedOrderIds
  | RemoveSelectedOrderIds
  | BulkRemoveSelectedOrderIds
  | ResetSelectedOrderIds;

const initialState: OrderSelectionState = {
  orderIds: [],
  allocatedOrderIds: [],
  selectedOrderIds: [],
};

const handlers: Record<
  string,
  (state: OrderSelectionState, action: Action) => OrderSelectionState
> = {
  SET_ORDER_IDS: (state, { value }: SetOrderIds): OrderSelectionState => ({
    ...state,
    orderIds: value,
  }),
  ADD_ORDER_IDS: (state, { value }: AddOrderIds): OrderSelectionState => ({
    ...state,
    orderIds: arrayUtil.uniq<number>([...state.orderIds, ...value]),
  }),
  REMOVE_ORDER_IDS: (state, { value }: RemoveOrderIds): OrderSelectionState => ({
    ...state,
    orderIds: state.orderIds.filter((it) => !value.includes(it)),
  }),
  RESET_ORDER_IDS: (state): OrderSelectionState => ({
    ...state,
    orderIds: [],
  }),
  SET_ALLOCATED_ORDER_IDS: (state, { value }: SetAllocatedOrderIds): OrderSelectionState => ({
    ...state,
    allocatedOrderIds: value,
  }),
  ADD_ALLOCATED_ORDER_IDS: (state, { value }: AddAllocatedOrderIds): OrderSelectionState => ({
    ...state,
    allocatedOrderIds: arrayUtil.uniq<number>([...state.allocatedOrderIds, ...value]),
  }),
  REMOVE_ALLOCATED_ORDER_IDS: (state, { value }: RemoveAllocatedOrderIds): OrderSelectionState => ({
    ...state,
    allocatedOrderIds: state.allocatedOrderIds.filter((it) => !value.includes(it)),
  }),
  RESET_ALLOCATED_ORDER_IDS: (state): OrderSelectionState => ({
    ...state,
    allocatedOrderIds: [],
  }),
  SET_SELECTED_ORDER_IDS: (state, { value }: SetSelectedOrderIds): OrderSelectionState => ({
    ...state,
    // payload を破壊しないようコピーしてからソートする (他の selected 系 handler と同じく新配列を対象にする)。
    selectedOrderIds: arrayUtil.uniq<number>([...value].sort()),
  }),
  ADD_SELECTED_ORDER_IDS: (state, { value }: AddSelectedOrderIds): OrderSelectionState => ({
    ...state,
    selectedOrderIds: arrayUtil.uniq<number>([...state.selectedOrderIds, value].sort()),
  }),
  BULK_ADD_SELECTED_ORDER_IDS: (state, { value }: BulkAddSelectedOrderIds): OrderSelectionState => ({
    ...state,
    selectedOrderIds: arrayUtil.uniq<number>([...state.selectedOrderIds, ...value].sort()),
  }),
  REMOVE_SELECTED_ORDER_IDS: (state, { value }: RemoveSelectedOrderIds): OrderSelectionState => ({
    ...state,
    selectedOrderIds: arrayUtil.uniq<number>(state.selectedOrderIds.filter((id) => id !== value).sort()),
  }),
  BULK_REMOVE_SELECTED_ORDER_IDS: (state, { value }: BulkRemoveSelectedOrderIds): OrderSelectionState => ({
    ...state,
    selectedOrderIds: arrayUtil.uniq<number>(state.selectedOrderIds.filter((id) => !value.includes(id)).sort()),
  }),
  RESET_SELECTED_ORDER_IDS: (state): OrderSelectionState => ({
    ...state,
    selectedOrderIds: [],
  }),
};

const reducer: (state: OrderSelectionState, action: Action) => OrderSelectionState = (
  state,
  action
) => (handlers[action.type] ? handlers[action.type](state, action) : state);

/* eslint-disable @typescript-eslint/no-unused-vars */
export const OrderSelectionContext: Context<OrderSelectionContextValue> = createContext<OrderSelectionContextValue>({
  orderIds: [],
  allocatedOrderIds: [],
  selectedOrderIds: [],
  setOrderIds(value: number[]): void {},
  addOrderIds(value: number[]): void {},
  removeOrderIds(value: number[]): void {},
  resetOrderIds(): void {},
  setAllocatedOrderIds(value: number[]): void {},
  addAllocatedOrderIds(value: number[]): void {},
  removeAllocatedOrderIds(value: number[]): void {},
  resetAllocatedOrderIds(): void {},
  setSelectedOrderIds(value: number[]): void {},
  addSelectedOrderIds(value: number): void {},
  bulkAddSelectedOrderIds(value: number[]): void {},
  removeSelectedOrderIds(value: number): void {},
  bulkRemoveSelectedOrderIds(value: number[]): void {},
  resetSelectedOrderIds(): void {},
});
/* eslint-enable @typescript-eslint/no-unused-vars */

export const OrderSelectionProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  // dispatch は React が安定性を保証するため useCallback([dispatch]) で参照を固定し、
  // PlansContext.handlePlanningResponse など context 境界を跨いで参照する側で
  // stale closure になっても同一挙動になるようにする。
  const setOrderIds = useCallback((value: number[]) => {
    dispatch({ type: 'SET_ORDER_IDS', value });
  }, []);
  const addOrderIds = useCallback((value: number[]) => {
    dispatch({ type: 'ADD_ORDER_IDS', value });
  }, []);
  const removeOrderIds = useCallback((value: number[]) => {
    dispatch({ type: 'REMOVE_ORDER_IDS', value });
  }, []);
  const resetOrderIds = useCallback(() => {
    dispatch({ type: 'RESET_ORDER_IDS' });
  }, []);
  const setAllocatedOrderIds = useCallback((value: number[]) => {
    dispatch({ type: 'SET_ALLOCATED_ORDER_IDS', value });
  }, []);
  const addAllocatedOrderIds = useCallback((value: number[]) => {
    dispatch({ type: 'ADD_ALLOCATED_ORDER_IDS', value });
  }, []);
  const removeAllocatedOrderIds = useCallback((value: number[]) => {
    dispatch({ type: 'REMOVE_ALLOCATED_ORDER_IDS', value });
  }, []);
  const resetAllocatedOrderIds = useCallback(() => {
    dispatch({ type: 'RESET_ALLOCATED_ORDER_IDS' });
  }, []);
  const setSelectedOrderIds = useCallback((value: number[]) => {
    dispatch({ type: 'SET_SELECTED_ORDER_IDS', value });
  }, []);
  const addSelectedOrderIds = useCallback((value: number) => {
    dispatch({ type: 'ADD_SELECTED_ORDER_IDS', value });
  }, []);
  const bulkAddSelectedOrderIds = useCallback((value: number[]) => {
    dispatch({ type: 'BULK_ADD_SELECTED_ORDER_IDS', value });
  }, []);
  const removeSelectedOrderIds = useCallback((value: number) => {
    dispatch({ type: 'REMOVE_SELECTED_ORDER_IDS', value });
  }, []);
  const bulkRemoveSelectedOrderIds = useCallback((value: number[]) => {
    dispatch({ type: 'BULK_REMOVE_SELECTED_ORDER_IDS', value });
  }, []);
  const resetSelectedOrderIds = useCallback(() => {
    dispatch({ type: 'RESET_SELECTED_ORDER_IDS' });
  }, []);

  const value = useMemo<OrderSelectionContextValue>(
    () => ({
      ...state,
      setOrderIds,
      addOrderIds,
      removeOrderIds,
      resetOrderIds,
      setAllocatedOrderIds,
      addAllocatedOrderIds,
      removeAllocatedOrderIds,
      resetAllocatedOrderIds,
      setSelectedOrderIds,
      addSelectedOrderIds,
      bulkAddSelectedOrderIds,
      removeSelectedOrderIds,
      bulkRemoveSelectedOrderIds,
      resetSelectedOrderIds,
    }),
    [
      state,
      setOrderIds,
      addOrderIds,
      removeOrderIds,
      resetOrderIds,
      setAllocatedOrderIds,
      addAllocatedOrderIds,
      removeAllocatedOrderIds,
      resetAllocatedOrderIds,
      setSelectedOrderIds,
      addSelectedOrderIds,
      bulkAddSelectedOrderIds,
      removeSelectedOrderIds,
      bulkRemoveSelectedOrderIds,
      resetSelectedOrderIds,
    ]
  );

  return (
    <OrderSelectionContext.Provider value={value}>
      {children}
    </OrderSelectionContext.Provider>
  );
};
